// @flow

import React from 'react';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n } from 'shared/utils';
import RecordPaymentMutation from 'main-app/mutations/RecordPayment';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
} from 'shared/components/form';

type Props = {
  onClose: () => void,
  invoiceId: string,
};

const PAYMENT_METHOD_OPTIONS = [
  { label: 'Cash', value: 'CASH' },
  { label: 'Check', value: 'CHECK' },
  { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
  { label: 'Credit Card', value: 'CREDIT_CARD' },
  { label: 'Other', value: 'OTHER' },
];

const RecordPaymentModal = ({ onClose, invoiceId }: Props) => (
  <Modal isOpen onClose={onClose}>
    <Formik
      initialValues={{
        amount: '',
        paymentMethod: 'BANK_TRANSFER',
        referenceNumber: '',
        paymentDate: null,
        notes: '',
      }}
      validationSchema={Yup.object().shape({
        amount: Yup.number()
          .required(i18n.t('Amount is required'))
          .positive(i18n.t('Amount must be positive')),
        paymentMethod: Yup.string().required(i18n.t('Payment method is required')),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await RecordPaymentMutation.commit({
            variables: {
              input: {
                invoiceId,
                amount: parseFloat(values.amount),
                paymentMethod: values.paymentMethod && typeof values.paymentMethod === 'object'
                  ? values.paymentMethod.value
                  : values.paymentMethod,
                referenceNumber: values.referenceNumber || null,
                paymentDate: values.paymentDate || null,
                notes: values.notes || null,
              },
            },
          });

          Actions.alertNotification(
            i18n.t('Payment recorded successfully'),
            'success',
          );
          onClose();
        } catch (e) {
          setSubmitting(false);
          Actions.alertNotification(e.message, 'error');
        }
      }}
      render={({
        errors,
        isValid,
        isSubmitting,
        handleSubmit,
      }) => (
        <Form>
          <ModalHeader header={i18n.t('Record Payment')} onClose={onClose} />
          <ModalBody withPadding>
            <FieldGroupRow
              left={
                <FieldGroup
                  name="amount"
                  label={i18n.t('Amount')}
                  placeholder="0.00"
                  error={errors.amount}
                />
              }
              right={
                <FieldGroup
                  name="paymentMethod"
                  type="select"
                  label={i18n.t('Payment Method')}
                  options={PAYMENT_METHOD_OPTIONS}
                  error={errors.paymentMethod}
                />
              }
            />
            <FieldGroupRow
              left={
                <FieldGroup
                  name="referenceNumber"
                  label={i18n.t('Reference Number')}
                  placeholder={i18n.t('Check #, Transaction ID, etc.')}
                  error={errors.referenceNumber}
                />
              }
              right={
                <FieldGroup
                  name="paymentDate"
                  type="date"
                  label={i18n.t('Payment Date')}
                  placeholder={i18n.t('Date')}
                  error={errors.paymentDate}
                  allowPastDates
                />
              }
            />
            <FieldGroup
              name="notes"
              type="textarea"
              label={i18n.t('Notes')}
              placeholder={i18n.t('Optional notes...')}
              error={errors.notes}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              theme="blue"
              disabled={!isValid}
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              {i18n.t('Record Payment')}
            </Button>
          </ModalFooter>
        </Form>
      )}
    />
  </Modal>
);

export default RecordPaymentModal;
